// Turns the flat category list from /api/categories/menu into the nested
// shape the Navbar mega-menu expects:
//   { categories: [{ id, name, sections: [{ id, name, items: [{ name, id }] }] }], pages: [] }
//
// The mega-menu shows three levels: tab -> section (header) -> item. The tabs are
// the top-level (level-1) categories when there is more than one (e.g. Men /
// Women / Kids); otherwise a single implicit top collapses and the level-2
// categories become the tabs.
//
// The catalog can be deeper than three levels, so for each tab we descend past
// any lone intermediate levels (a single-child chain like Men > *Clothing* >
// Shirts/Trousers) and show the deepest meaningful grouping: the leaves' parents
// become the section headers and the leaves become the items. There is no
// "View All" entry — clicking a header opens that category's listing directly.

const parentIdOf = (c) =>
  c?.parentCategory?._id || c?.parentCategory || null;

export function buildNavigation(categories) {
  if (!Array.isArray(categories) || categories.length === 0) return null;

  const kidsOf = (id) =>
    categories.filter((c) => String(parentIdOf(c)) === String(id));

  const topCount = categories.filter((c) => c.level === 1).length;
  const tabLevel = topCount > 1 ? 1 : 2;

  // Tabs follow the order categories were created in (the API returns them
  // sorted by level then _id), so the storefront matches the admin ordering.
  const tabs = categories.filter((c) => c.level === tabLevel);

  const cats = tabs.map((tab) => {
    // Start at the tab's children, then skip lone intermediate levels so the
    // dropdown shows the deepest meaningful grouping.
    let secs = kidsOf(tab._id);
    while (secs.length === 1 && kidsOf(secs[0]._id).length > 0) {
      secs = kidsOf(secs[0]._id);
    }

    // No children -> no dropdown. The navbar renders such a tab as a direct
    // link to the category's listing.
    if (secs.length === 0) {
      return { id: tab.name, name: tab.title, sections: [] };
    }

    const anyHasItems = secs.some((s) => kidsOf(s._id).length > 0);

    // All sections are leaves -> a single column of items (no headers).
    if (!anyHasItems) {
      const items = secs.map((s) => ({ name: s.title, id: s.name }));
      return {
        id: tab.name,
        name: tab.title,
        sections: [{ id: tab.name, name: tab.title, items }],
      };
    }

    const sections = secs.map((s) => {
      const kids = kidsOf(s._id);
      const items =
        kids.length > 0
          ? kids.map((c) => ({ name: c.title, id: c.name }))
          : // leaf section -> single item pointing at the section itself
            [{ name: s.title, id: s.name }];
      return { id: s.name, name: s.title, items };
    });

    return { id: tab.name, name: tab.title, sections };
  });

  if (cats.length === 0) return null;

  return { categories: cats, pages: [] };
}
